import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { Shield, Heart, Star, Users } from 'lucide-react';

const coreValues = [
  {
    icon: Shield,
    title: 'Integrity',
    description: 'We uphold honesty and transparency in every decision and action.',
  },
  {
    icon: Heart,
    title: 'Compassion',
    description: 'We approach every interaction with empathy, kindness, and respect.',
  },
  {
    icon: Star,
    title: 'Excellence',
    description: 'We are committed to the highest standards in service and performance.',
  },
  {
    icon: Users,
    title: 'Collaboration',
    description: 'We believe in the power of teamwork to achieve shared success.',
  },
];

export const AboutUs = () => {
  const [contentRef, contentVisible] = useScrollAnimation();
  const [valuesRef, valuesVisible] = useScrollAnimation();

  return (
    <section
      id="about"
      data-testid="about-section"
      className="section-padding bg-secondary relative texture-overlay"
    >
      <div className="container-width relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-sm font-medium tracking-widest uppercase text-primary/60 mb-4">
            About Us
          </span>
          <h2 className="text-4xl md:text-5xl font-semibold tracking-tight text-primary">
            Your Trusted Healthcare Staffing Partner
          </h2>
        </div>

        {/* About Content */}
        <div
          ref={contentRef}
          className={`grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-24 animate-on-scroll ${
            contentVisible ? 'visible' : ''
          }`}
        >
          {/* Image */}
          <div className="relative">
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/3c79fc15-7acb-4945-a01c-dd7a9cf21362/images/9e0586f716a99fc98a2bb291ba7f619c38a862483c9ba73a70400b0dd5d2538c.png"
                alt="Diverse healthcare staffing team collaborating in modern office"
                className="w-full h-[400px] lg:h-[500px] object-cover"
                loading="lazy"
                data-testid="about-image"
              />
            </div>
            {/* Decorative element */}
            <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-primary/10 rounded-full blur-2xl" />
          </div>

          {/* Text Content */}
          <div>
            <p className="text-lg md:text-xl leading-relaxed text-muted-foreground">
              Founded in 2019, our organization was built on a clear and powerful
              mission: to enhance healthcare delivery by connecting passionate
              professionals with purpose-driven facilities. We believe that
              exceptional patient care starts with the right people in the right
              places—individuals who are not only skilled, but also deeply
              committed to making a difference.
            </p>
            <p className="text-lg md:text-xl leading-relaxed text-muted-foreground mt-6">
              Over the years, we have partnered with hundreds of healthcare
              facilities across the United States, helping them maintain high
              standards of care by providing access to compassionate, qualified
              staff. Our story is one of dedication, growth, and a steadfast
              commitment to bridging the gap between healthcare talent and the
              communities that need them most.
            </p>
          </div>
        </div>

        {/* Core Values */}
        <div className="text-center mb-12">
          <h3 className="text-3xl font-semibold text-primary">Core Values</h3>
        </div>

        <div
          ref={valuesRef}
          className={`grid sm:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children ${
            valuesVisible ? 'visible' : ''
          }`}
        >
          {coreValues.map((value, index) => (
            <div
              key={index}
              className="value-card text-center"
              data-testid={`value-card-${index}`}
            >
              <div className="icon-container mx-auto mb-4">
                <value.icon className="h-6 w-6" />
              </div>
              <h4 className="text-xl font-semibold text-primary mb-2">
                {value.title}
              </h4>
              <p className="text-muted-foreground text-sm">{value.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default AboutUs;
